// src/app/services/stripe.service.ts
import { inject, Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { getFunctions, httpsCallable } from '@angular/fire/functions';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { NGXLogger } from 'ngx-logger';
import { environment } from '../../environments/environment';
// import { Subscription } from '../models/subscription.model';

@Injectable({ providedIn: 'root' })
export class StripeService {
  private afs: AngularFirestore = inject(AngularFirestore);
  private logger: NGXLogger = inject(NGXLogger);
  stripe: Stripe | null = null;

  constructor() {
    loadStripe(environment.STRIPE_PUBLISHABLE_KEY).then((stripe) => {
      this.stripe = stripe;
    });
  }

  async createSubscription(
    groupId: string,
    setupIntentId: string | null,
    priceIds?: string[],
    seats?: number,
    items?: { priceId: string; quantity: number }[],
    promoCode?: string,
  ) {
    const functions = getFunctions();

    const fn = httpsCallable(functions, 'payment_createSubscription', {
      limitedUseAppCheckTokens: true,
    });

    const result = await fn({
      groupId,
      setupIntentId,
      priceIds,
      seats,
      items,
      promoCode,
    });
    return result.data;
  }

  async getSubscriptionFromSetupIntent(setupIntentId: string) {
    const functions = getFunctions();
    const fn = httpsCallable(
      functions,
      'payment_getSubscriptionFromSetupIntent',
      {
        limitedUseAppCheckTokens: true,
      },
    );
    const result = await fn({ setupIntentId });
    return result.data;
  }

  async getGroupSubscriptions(groupId: string): Promise<any[]> {
    this.logger.debug('getGroupSubscriptions called for group:', groupId);
    return new Promise((resolve, reject) => {
      const subscriptions = this.afs.collection<any>('subscriptions', (ref) =>
        ref.where('groupId', '==', groupId),
      );

      subscriptions.get().subscribe({
        next: (querySnapshot) => {
          const subscriptionList: any[] = [];
          querySnapshot.forEach((doc) => {
            this.logger.debug('Subscription found:', doc.id, '=>', doc.data());
            subscriptionList.push({ id: doc.id, ...doc.data() });
          });
          resolve(subscriptionList);
        },
        error: (err) => {
          this.logger.error('Error fetching subscriptions:', err);
          reject(err);
        },
      });
    });
  }

  async deleteSubscription(subscriptionId: string) {
    const functions = getFunctions();
    const fn = httpsCallable(functions, 'payment_deleteSubscription', {
      limitedUseAppCheckTokens: true,
    });
    const result = await fn({ subscriptionId });
    return result.data;
  }

  async deleteSubscriptionItem(
    subscriptionId: string,
    subscriptionItemId: string,
  ) {
    this.logger.debug('StripeService.deleteSubscriptionItem called:', { subscriptionId, subscriptionItemId });
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'payment_deleteSubscriptionItem', {
        limitedUseAppCheckTokens: true,
      });
      const result = await fn({ subscriptionId, subscriptionItemId });
      this.logger.debug('StripeService.deleteSubscriptionItem result:', result);
      return result.data;
    } catch (err: any) {
      this.logger.error('StripeService.deleteSubscriptionItem failed:', err);
      console.error('StripeService.deleteSubscriptionItem error:', err);
      console.dir(err);
      throw err;
    }
  }

  async getGroupBills(groupId: string): Promise<any[]> {
    const functions = getFunctions();
    const fn = httpsCallable(functions, 'payment_getGroupBills', {
      limitedUseAppCheckTokens: true,
    });
    const result = await fn({ groupId });
    return result.data as any[];
  }
}
